// ---------------------------------------------------------------------------
// Menu tree filter — prune a nested menu tree to nodes matching a query
// ---------------------------------------------------------------------------
//
// Used by the sidebar nav search. Pure: returns a pruned tree, never mutates
// the input. Matching is diacritic- and case-insensitive on the node label.
//
// Keep rules:
//   - Leaf: kept iff its own label matches.
//   - Group: kept iff its own label matches OR it has ≥1 kept descendant.
//     When a group's OWN label matches, ALL its original children are kept
//     (so searching a section name reveals the whole section); otherwise only
//     the matching descendants survive.

/**
 * Filter a menu tree by a label query.
 * Empty/whitespace query returns the input tree unchanged (same reference).
 *
 * @param {import('../../contracts/menu.js').MenuNode[]} tree
 * @param {string} query
 * @returns {import('../../contracts/menu.js').MenuNode[]}
 */
export function filterMenuTree(tree, query) {
  const q = normalize(query);
  if (q === '') return tree;

  /** @type {import('../../contracts/menu.js').MenuNode[]} */
  const result = [];
  for (const node of tree) {
    const kept = filterNode(node, q);
    if (kept) result.push(kept);
  }
  return result;
}

/**
 * @param {import('../../contracts/menu.js').MenuNode} node
 * @param {string} q - normalized, non-empty query
 * @returns {import('../../contracts/menu.js').MenuNode | null}
 */
function filterNode(node, q) {
  const selfMatches = normalize(node.label).includes(q);

  if (node.children.length === 0) {
    // Leaf — kept only on its own match.
    return selfMatches ? node : null;
  }

  // Group whose own label matches → keep the whole subtree intact.
  if (selfMatches) return node;

  // Otherwise keep only matching descendants; drop the group if none survive.
  /** @type {import('../../contracts/menu.js').MenuNode[]} */
  const keptChildren = [];
  for (const child of node.children) {
    const kept = filterNode(child, q);
    if (kept) keptChildren.push(kept);
  }
  if (keptChildren.length === 0) return null;

  return { ...node, children: keptChildren };
}

/**
 * Diacritic- and case-insensitive normalization for substring matching.
 * @param {string} s
 * @returns {string}
 */
function normalize(s) {
  return s.trim().toLowerCase().normalize('NFD').replace(/[̀-ͯ]/g, '');
}
